import {
  createTheme,
  type MantineColorsTuple,
  type MantineThemeOverride,
} from '@mantine/core';
import { colors, displaySize, fonts, fontSizes, radii } from './tokens';

/**
 * Mantine 7 theme — wires Forge design tokens into Mantine's primitive tokens.
 *
 * Mantine requires a 10-shade tuple per color. We seed a tuple around each
 * accent value; shades 4–7 are the truthful tones. Visual designers can
 * later replace these via Mantine's color generator if a richer palette
 * is needed.
 */

const neonShades: MantineColorsTuple = [
  '#e7fbf0',
  '#cdf3dd',
  '#9be6b9',
  '#65d893',
  '#3ace74',
  colors.neon, // 5 — primary
  '#1bb14e',
  '#108a3c',
  '#08622a',
  '#033a18',
];

const cyanShades: MantineColorsTuple = [
  '#e3fafe',
  '#c6f3fc',
  '#8be7f9',
  '#4eddf6',
  '#22d3ee', // hud-cyan (4)
  '#0bb8d6',
  '#0492a8',
  '#026d7e',
  '#014754',
  '#00272f',
];

const amberShades: MantineColorsTuple = [
  '#fff8e3',
  '#feefc1',
  '#fde07a',
  '#fcce3f',
  '#fbbf24', // hud-amber (4)
  '#e0a610',
  '#b48008',
  '#875c05',
  '#5a3c02',
  '#2d1d01',
];

const redShades: MantineColorsTuple = [
  '#fdecec',
  '#fbd5d5',
  '#f7adad',
  '#f48a8a',
  '#f87171', // hud-red (4)
  '#e74e4e',
  '#c12d2d',
  '#921e1e',
  '#621414',
  '#310a0a',
];

const magentaShades: MantineColorsTuple = [
  '#fdebfd',
  '#f9d2f9',
  '#f1a6f1',
  '#ec85ec',
  '#e879f9', // hud-magenta (4)
  '#c050d4',
  '#963aa6',
  '#6c2879',
  '#48184c',
  '#250a26',
];

/**
 * Neutral / void surfaces — Mantine's "dark" palette.
 *
 * MUST be static hex. Mantine's color helpers (`parseThemeColor`, `luminance`,
 * `darken`, `alpha`, and `autoContrast`) cannot parse `rgb(var(--x))`; shades
 * 6-9 previously held CSS-var strings, which painted correctly but returned
 * garbage from any derived computation. These are the literal dark-palette
 * values from styles/global.css. Theme-aware surfaces come from the `--c-*`
 * tokens in the UI kit, not from this tuple.
 */
const voidShades: MantineColorsTuple = [
  '#d8d8da',
  '#a9a9ad',
  '#7c7c81',
  '#5c5c61',
  '#3f3f43',
  '#2a2a2d',
  '#18181b', // 6 — card surface
  '#18181b', // 7 — panel
  '#0f0f12', // 8 — deep
  '#09090b', // 9 — void (root body bg)
];

export const forgeTheme: MantineThemeOverride = createTheme({
  primaryColor: 'neon',
  primaryShade: { light: 6, dark: 5 },
  colors: {
    neon: neonShades,
    cyan: cyanShades,
    amber: amberShades,
    red: redShades,
    magenta: magentaShades,
    dark: voidShades,
  },
  white: '#ffffff',
  black: '#0a0a0a',
  fontFamily: fonts.body,
  fontFamilyMonospace: fonts.mono,
  headings: {
    fontFamily: fonts.display,
    fontWeight: '600',
    sizes: {
      // Pinned to the display size, NOT fontSizes.xl — xl is a body step (20px)
      // and headings must not shrink with it.
      h1: { fontSize: `${displaySize}px`, lineHeight: '1.2' },
      h2: { fontSize: '24px', lineHeight: '1.25' },
      h3: { fontSize: '20px', lineHeight: '1.3' },
      h4: { fontSize: `${fontSizes.lg}px`, lineHeight: '1.35' },
      h5: { fontSize: `${fontSizes.md}px`, lineHeight: '1.4' },
      h6: { fontSize: `${fontSizes.sm}px`, lineHeight: '1.45' },
    },
  },
  fontSizes: {
    xs: `${fontSizes.xs}px`,
    sm: `${fontSizes.sm}px`,
    md: `${fontSizes.md}px`,
    lg: `${fontSizes.lg}px`,
    xl: `${fontSizes.xl}px`,
  },
  radius: {
    xs: `${radii.xs}px`,
    sm: `${radii.sm}px`,
    md: `${radii.md}px`,
    lg: `${radii.lg}px`,
    xl: `${radii.xl}px`,
  },
  defaultRadius: 'md',
  cursorType: 'pointer',
  focusRing: 'auto',
  autoContrast: true,
});
